import { Card, ErrorBox, Link, Page, Stat, Table, Td } from '@elsa/ui';
import { useQuery } from '@tanstack/react-query';
import { fmtDateTime, fmtDay, fmtLei, fmtNum } from '../lib/format.ts';
import { rpc } from '../lib/supabase.ts';
import type { Acquisition, EntityInfo, LockedItem } from '../lib/types.ts';
import { SkeletonCards, SkeletonLines, SkeletonTable } from './loading.tsx';
import { Blurred, LockedNote, LockIcon, OutsidePlan } from './locked.tsx';

type Item = Acquisition['items'][number];

/** Un produs pe un cod din afara abonamentului: doar codul; restul e un inlocuitor estompat. */
function LockedRow({ l }: { l: LockedItem }) {
  return (
    <tr className="bg-gri-100">
      <Td className="max-w-md">
        <div className="flex items-center gap-1.5 text-gri-800">
          <LockIcon />
          <Blurred>Denumirea produsului din achiziție</Blurred>
          <span className="sr-only">Produs blocat: codul nu e în abonamentul firmei</span>
        </div>
        <div className="mt-1">
          <OutsidePlan label="cod în afara abonamentului" />
        </div>
      </Td>
      <Td className="whitespace-nowrap">
        {l.cpv_code ?? '—'}
        {l.cpv_label ? <div className="text-xs text-gri-700">{l.cpv_label}</div> : null}
      </Td>
      <Td className="whitespace-nowrap">
        <Blurred>000 buc</Blurred>
      </Td>
      <Td className="whitespace-nowrap text-right">
        <Blurred>00,00 RON</Blurred>
      </Td>
      <Td className="whitespace-nowrap text-right">
        <Blurred>000,00 RON</Blurred>
      </Td>
    </tr>
  );
}

function Party({
  title,
  e,
  text,
  to,
}: {
  title: string;
  e: EntityInfo | null;
  text: string | null;
  to: string | null;
}) {
  return (
    <Card title={title}>
      <div className="space-y-1 text-sm">
        <div className="font-medium">
          {to && e ? (
            <Link to={to} className="hover:underline">
              {e.entity_name ?? text}
            </Link>
          ) : (
            (e?.entity_name ?? text ?? '—')
          )}
        </div>
        {e ? (
          <div className="text-gri-800">
            {e.numeric_fiscal_number ? <div>CUI {e.numeric_fiscal_number}</div> : null}
            <div>{[e.address, e.city, e.county].filter(Boolean).join(', ')}</div>
            {e.email ? <div>{e.email}</div> : null}
            {e.phone ? <div>{e.phone}</div> : null}
          </div>
        ) : null}
      </div>
    </Card>
  );
}

export function AcquisitionPage({ id }: { id: number }) {
  const q = useQuery({
    queryKey: ['acquisition', id],
    queryFn: () => rpc<Acquisition | null>('acquisition', { p_id: id }),
  });
  if (q.isLoading) {
    return (
      <Page title="Achiziția">
        <SkeletonCards count={4} />
        <Card title="Detalii">
          <SkeletonLines lines={3} />
        </Card>
        <Card title="Produse">
          <SkeletonTable rows={3} cols={5} />
        </Card>
      </Page>
    );
  }
  if (q.error) return <Page title="Achiziția">{<ErrorBox error={q.error} />}</Page>;
  const a = q.data;
  if (!a) {
    return (
      <Page title="Achiziția nu se vede">
        <p className="text-sm text-gri-800">
          Achiziția nu există sau e în afara istoricului permis de planul firmei.
        </p>
      </Page>
    );
  }
  const total = a.items.reduce((s, i) => s + Number(i.line_total_ron ?? 0), 0);
  const locked = a.locked_items ?? [];
  const lockedCodes = [...new Set(locked.map((l) => l.cpv_code ?? '—'))];
  // Produsele vizibile si cele blocate, in ordinea din SEAP (id-ul produsului).
  const rows: ({ kind: 'item'; i: Item } | { kind: 'locked'; l: LockedItem })[] = [
    ...a.items.map((i) => ({ kind: 'item' as const, i })),
    ...locked.map((l) => ({ kind: 'locked' as const, l })),
  ].sort((x, y) => (x.kind === 'item' ? x.i.id : x.l.id) - (y.kind === 'item' ? y.i.id : y.l.id));
  return (
    <Page title={a.name ?? `Achiziția ${a.id}`}>
      <div className="grid gap-3 sm:grid-cols-4">
        <Stat
          label="Data finalizării"
          value={fmtDay(a.finalization_day)}
          sub={fmtDateTime(a.finalization_date)}
        />
        <Stat label="Valoare finală" value={fmtLei(a.closing_value_ron)} />
        <Stat label="Valoare estimată" value={fmtLei(a.estimated_value_ron)} />
        <Stat label="Cod SEAP" value={a.unique_identification_code ?? '—'} sub={`id ${a.id}`} />
      </div>
      <Card title="Detalii">
        <dl className="grid gap-2 text-sm sm:grid-cols-[10rem_1fr]">
          <dt className="text-gri-700">CPV</dt>
          <dd>
            {a.cpv_code && a.cpv_in_plan !== false ? (
              <Link to={`/app/preturi/${a.cpv_code}`} className="hover:underline">
                {a.cpv_code}
              </Link>
            ) : (
              (a.cpv_code ?? '—')
            )}{' '}
            {a.cpv_label} {a.cpv_in_plan === false ? <OutsidePlan /> : null}
          </dd>
          <dt className="text-gri-700">Publicată</dt>
          <dd>{fmtDateTime(a.publication_date)}</dd>
          {a.description ? (
            <>
              <dt className="text-gri-700">Descriere</dt>
              <dd className="whitespace-pre-line">{a.description}</dd>
            </>
          ) : null}
        </dl>
      </Card>
      <div className="grid gap-3 sm:grid-cols-2">
        <Party
          title="Autoritatea contractantă"
          e={a.authority}
          text={a.contracting_authority_text}
          to={a.contracting_authority_id ? `/app/autoritati/${a.contracting_authority_id}` : null}
        />
        <Party
          title="Furnizorul"
          e={a.supplier}
          text={a.supplier_text}
          to={a.supplier_id ? `/app/furnizori/${a.supplier_id}` : null}
        />
      </div>
      <Card
        title={`Produse (${a.items.length + locked.length})`}
        actions={
          <span className="text-sm tabular-nums">
            {fmtLei(total)}
            {locked.length ? <span className="text-gri-700"> fără cele blocate</span> : null}
          </span>
        }
      >
        {locked.length ? (
          <div className="mb-3">
            <LockedNote count={locked.length} codes={lockedCodes} />
          </div>
        ) : null}
        <Table head={['Produsul', 'CPV', 'Cantitate', 'Preț unitar', 'Total']} empty={!rows.length}>
          {rows.map((row) => {
            if (row.kind === 'locked') return <LockedRow key={`l${row.l.id}`} l={row.l} />;
            const i = row.i;
            return (
              <tr key={i.id}>
                <Td className="max-w-md">
                  <div className="font-medium">{i.name}</div>
                  {i.description ? (
                    <div className="text-xs text-gri-700">{i.description}</div>
                  ) : null}
                </Td>
                <Td className="whitespace-nowrap">
                  {i.cpv_code ? (
                    <Link to={`/app/preturi/${i.cpv_code}`} className="hover:underline">
                      {i.cpv_code}
                    </Link>
                  ) : (
                    '—'
                  )}
                </Td>
                <Td className="whitespace-nowrap tabular-nums">
                  {fmtNum(i.quantity)} {i.measure_unit}
                </Td>
                <Td className="whitespace-nowrap text-right tabular-nums">
                  {fmtLei(i.unit_price_ron)}
                </Td>
                <Td className="whitespace-nowrap text-right tabular-nums">
                  {fmtLei(i.line_total_ron)}
                </Td>
              </tr>
            );
          })}
        </Table>
      </Card>
    </Page>
  );
}
